import { useQuery } from "@tanstack/react-query";
import { Button, Empty, message, Spin, Typography } from "antd";
import moment from "moment";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, useNavigate } from "react-router-dom";

import { getGlobalCurrency } from "shared/lib/utils";
import ROUTES from "shared/routes";
import { getTasks } from "../api";
import { GetTasksFilter } from "../types";
import styles from "./index.module.scss";

export const TasksList = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [messageApi, contextHolder] = message.useMessage();

  const [filter, seFilter] = useState<GetTasksFilter>("all");

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["tasks", filter],
    queryFn: () => getTasks({ filter }),
  });

  useEffect(() => {
    if (isError) {
      messageApi.error(
        (error as any)?.response?.data?.message ||
          "An error occurred while receiving the list of tasks",
      );
    }
  }, [isError, error, messageApi]);

  if (isLoading) {
    return (
      <div className={styles.listWrapperLoader}>
        {contextHolder}
        <Spin />
      </div>
    );
  }

  return (
    <>
      <div className={styles.filter}>
        <Button
          type={filter === "all" ? "primary" : undefined}
          onClick={() => {
            seFilter("all");
          }}
        >
          Все
        </Button>
        <Button
          type={filter === "in_progress" ? "primary" : undefined}
          onClick={() => {
            seFilter("in_progress");
          }}
        >
          Принятые задания
        </Button>
        <Button
          type={filter === "archive" ? "primary" : undefined}
          onClick={() => {
            seFilter("archive");
          }}
        >
          Архивные
        </Button>
      </div>

      {!data?.data?.length ? (
        <div className={styles.listWrapper}>
          {contextHolder}
          <Empty
            description={
              <Typography.Text>
                {isError
                  ? (error as any)?.response?.data?.message ||
                    t("tasks.error_message")
                  : t("tasks.list_empty")}
              </Typography.Text>
            }
          />
        </div>
      ) : (
        <div className={styles.list}>
          {data?.data?.map((item) => (
            <div
              className={styles.listItem}
              key={item.id}
              onClick={() => {
                navigate(
                  generatePath(ROUTES.TASKS_ID, {
                    taskId: item.id,
                  }),
                );
              }}
            >
              <div className={styles.head}>
                <div className={styles.taskNumber}>
                  {t("tasks.task_number")} {item.number}
                </div>

                {item.status?.code &&
                  {
                    DRAFT: t("tasks.status_draft"),
                    SEARCH_EXECUTOR: t("tasks.status_search_executor"),
                    WAIT_ANSWER: t("tasks.status_wait_answer"),
                    IN_PROGRESS: t("tasks.status_in_progress"),
                    COMPLETED: t("tasks.status_completed"),
                    CANCELLED: t("tasks.status_cancelled"),
                  }[item.status.code]}
              </div>

              <div className={styles.title}>{item.name}</div>

              <ul className={styles.description}>
                {item.services?.map((item, index) => (
                  <li key={index}>{item?.fields?.description}</li>
                ))}
              </ul>

              <div className={styles.footer}>
                <div className={styles.footerBody}>
                  <div className={styles.price}>
                    {getGlobalCurrency(item.total)}
                  </div>
                  <div className={styles.date}>
                    {moment(item.date_finish).format("DD.MM.YYYY")}
                  </div>
                </div>
                <Button
                  className={styles.viewButton}
                  onClick={() => {
                    navigate(
                      generatePath(ROUTES.TASKS_ID, {
                        taskId: item.id,
                      }),
                    );
                  }}
                >
                  {t("tasks.view_button")}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
};
